/**
 * Auth public outlet — feature-gates the entire `/auth/*` namespace.
 *
 * Pages inside this outlet (Register / VerifyOtp / ChoosePerson)
 * MUST consume the unified managers from `@hero/web-hooks`:
 *   • `usePhoneNumberManager`  — sends OTP and persists phone
 *     response.
 *   • `useOtpVerifyManager`    — verifies OTP via the unified
 *     `auth.signIn` and routes new vs existing users.
 *   • `useSignUpWizardManager` — drives the persona / company /
 *     driver / profile steps and submits via `auth.signUp`.
 *
 * Direct `fetch(...)` + Redux `authSlice` flows are deprecated —
 * see `docs/relations-v2/08-user-identity.md`.
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import { useFeature } from '@hero/web-context';

export interface AuthPublicOutletProps {
  children: React.ReactNode;
}

/**
 * Wraps public auth routes (`/auth/*`). When both `sub_sign_in` and `sub_new_sign_up`
 * are off, the phone entry flow is disabled. When only one is off, optional notices
 * appear on `/auth/register` so operators can close sign-up or sign-in independently.
 */
export function AuthPublicOutlet({ children }: AuthPublicOutletProps) {
  const { t } = useTranslation('c-main');
  const { pathname } = useLocation();
  const { isEnabled: canSignIn } = useFeature('auth_module', 'sub_sign_in');
  const { isEnabled: canNewSignUp } = useFeature('auth_module', 'sub_new_sign_up');

  const hasAuthEntry = canSignIn || canNewSignUp;
  const isRegisterRoute = pathname === '/auth/register' || pathname.endsWith('/auth/register');

  if (!hasAuthEntry) {
    return (
      <div className="w-full px-2 py-8 text-center">
        <h1 className="text-lg font-semibold text-gray-900">
          {t('auth_gate.entry_disabled_title')}
        </h1>
        <p className="mt-3 text-sm text-muted-foreground">
          {t('auth_gate.entry_disabled_message')}
        </p>
      </div>
    );
  }

  return (
    <>
      {isRegisterRoute && !canNewSignUp && canSignIn ? (
        <p className="mb-4 rounded-md bg-amber-50 px-3 py-2 text-start text-sm text-amber-900">
          {t('auth_gate.new_sign_up_closed_notice')}
        </p>
      ) : null}
      {isRegisterRoute && canNewSignUp && !canSignIn ? (
        <p className="mb-4 rounded-md bg-amber-50 px-3 py-2 text-start text-sm text-amber-900">
          {t('auth_gate.sign_in_closed_notice')}
        </p>
      ) : null}
      {children}
    </>
  );
}
